import { useSelector, useDispatch } from 'react-redux';
import { selectTenant } from 'state/tenant';
import { useCallback } from 'react';
import log from 'loglevel';
import { minBy, reduce } from 'ramda';
import haversine from 'haversine';

import useGeocode, { GeocodingError } from 'hooks/useGeocode';

import useStoreLocations, {
  StoreLocation,
  StoreLocationsError,
} from 'hooks/requests/storeLocations';

import Coords from 'state/Coords';

import { AddressObject } from './useGeocode';

import {
  setClientVariable as setCV,
  mergeApplication,
  selectCreditApplication,
} from 'state/creditApplication';

import catchOnly from 'utils/catchOnly';

import { NonClientError } from './requests/fetchUtils';

const closestStore = (
  storeLocations: StoreLocation[],
  position: Coords,
): StoreWithDistance => {
  const pos: [number, number] = [position.latitude, position.longitude];

  const distance = (store: StoreLocation): number =>
    haversine(pos, [store.lat, store.long], {
      format: '[lat,lon]',
      unit: 'mile',
    });

  const store = reduce(minBy(distance), storeLocations[0], storeLocations);
  const dist = distance(store);
  return { storeNumber: store.storeNumber, distance: dist };
};

interface StoreWithDistance {
  storeNumber: string;
  distance: number;
}

type LegacyStoreAttributionHook = () => [
  (
    address: AddressObject,
    position: Coords,
  ) => Promise<StoreWithDistance | undefined>,
  boolean,
];

/**
 * fetch the list of store locations for the tenant, find the nearest store to
 * the current location.
 * Set the storeNumber on the credit application.
 */
const useLegacyStoreAttribution: LegacyStoreAttributionHook = () => {
  const [shouldGeocode, requestGeocode] = useGeocode();
  const tenant = useSelector(selectTenant);
  const { clientVariable3 } = useSelector(selectCreditApplication);
  const [fetchStoreLocations] = useStoreLocations();
  const dispatch = useDispatch();
  const shouldUseLegacyAttributeStore = Boolean(tenant.geolocationAttribution);

  const onClosestStoreFound = useCallback(
    (storeNumber: NonNullable<StoreLocation['storeNumber']>) => {
      dispatch(mergeApplication({ storeNumber }));
    },
    [dispatch],
  );

  const geocode = useCallback(
    async (values: Partial<AddressObject>) => {
      let position = null;
      try {
        position = await requestGeocode(values);
        if (!clientVariable3) {
          dispatch(setCV(3, 'G', 15));
        }
      } catch (e) {
        if (catchOnly(e, NonClientError, GeocodingError)) {
          if (!clientVariable3) {
            dispatch(setCV(3, 'E', 15));
          }
          log.error(e.name, e);
        }
      }
      return position;
    },
    [requestGeocode, clientVariable3, dispatch],
  );

  const attributeStore = useCallback(
    async (address: AddressObject, position?: Coords | null) => {
      if (!tenant.geolocationAttribution) return;

      if (!position && shouldGeocode) {
        position = await geocode(address);
      }

      if (!position) return;

      try {
        const storeLocations = await fetchStoreLocations(tenant);
        const storeWithDistance = closestStore(storeLocations, position);
        onClosestStoreFound(storeWithDistance.storeNumber);
        return storeWithDistance;
      } catch (error) {
        if (error instanceof StoreLocationsError) {
          log.error(error);
        } else {
          throw error;
        }
      }
    },
    [fetchStoreLocations, geocode, onClosestStoreFound, shouldGeocode, tenant],
  );

  return [attributeStore, shouldUseLegacyAttributeStore];
};

export default useLegacyStoreAttribution;
